/**
 * AssetPickerModal — full-screen-ish window for choosing an avatar, voice, or
 * style. Rendered through a PORTAL to document.body: ancestors use `.glass`
 * (backdrop-filter), which traps position:fixed children in a local stacking
 * context — the portal is what keeps this above the whole page.
 *
 * Avatar sources, both backend generations supported:
 *  - new (post-redeploy): flat stock list, each card directly pickable
 *  - legacy (currently deployed): group cards → looks drill-in. HeyGen's looks
 *    endpoint 400s for ~60% of groups (their corrupt created_at data); those
 *    show a friendly "pack unavailable" note instead of crashing.
 *
 * Owned avatars self-heal their preview image via resolveOwnedAvatarMedia.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import {
  X, Search, Play, Square, Loader2, Plus, Camera, Mic, Wand2, ShieldCheck,
  RefreshCw, Check, ChevronLeft, Clock, Sparkles, Video,
} from 'lucide-react';
import { cn } from '@/v2/utils';
import { v2Service } from '@/api/v2Service';
import { useUserStore } from '@/store/userStore';
import { getRecents, pushRecent } from '@/v2/recents';
import { stopPreview, togglePreview, isPreviewPlaying, isPreviewLoading, onPreviewChange } from '@/v2/audio';
import { resolveOwnedAvatarMedia } from '@/v2/avatarPreview';
import { useGenderLabel } from '@/v2/utils/gender';
import type { V2OwnedAvatar, V2AvatarGroup, V2AvatarCharacter, V2Voice, V2OwnedVoice, V2Brand } from '@/api/v2Types';
import { toast } from '@/v2/toast';

export type PickKind = 'avatar' | 'voice' | 'brand';
export interface PickedAsset {
  kind: PickKind;
  id: string;
  label: string;
  imageUrl?: string;
  previewAudioUrl?: string;
  gender?: string;
}

interface Props {
  kind: PickKind;
  selectedId?: string;
  onClose: () => void;
  onSelect: (item: PickedAsset) => void;
  /** Open a creation flow; the parent renders the actual modal. */
  onCreate?: (action: 'avatar-ai' | 'avatar-photo' | 'avatar-twin' | 'voice-clone' | 'voice-design' | 'brand-new') => void;
}

const titlesFor = (kind: PickKind): { title: string; sub: string } => {
  const map: Record<PickKind, { title: string; sub: string }> = {
    avatar: { title: i18n.t('assetPicker.avatarTitle', { ns: 'v2' }), sub: i18n.t('assetPicker.avatarSub', { ns: 'v2' }) },
    voice: { title: i18n.t('assetPicker.voiceTitle', { ns: 'v2' }), sub: i18n.t('assetPicker.voiceSub', { ns: 'v2' }) },
    brand: { title: i18n.t('assetPicker.brandTitle', { ns: 'v2' }), sub: i18n.t('assetPicker.brandSub', { ns: 'v2' }) },
  };
  return map[kind];
};

const NOT_DEPLOYED = () => i18n.t('assetPicker.notDeployed', { ns: 'v2' });

function Tabs<T extends string>({ tabs, active, onChange, label }: { tabs: readonly T[]; active: T; onChange: (t: T) => void; label?: (t: T) => string }) {
  return (
    <div className="flex items-center gap-6">
      {tabs.map((t) => (
        <button
          key={t}
          onClick={() => onChange(t)}
          className={cn(
            'relative pb-3 text-sm font-semibold whitespace-nowrap transition-colors',
            active === t ? 'text-text-primary' : 'text-text-tertiary hover:text-text-secondary'
          )}
        >
          {label ? label(t) : t}
          <span className={cn(
            'absolute left-0 right-0 bottom-0 h-[2.5px] rounded-full bg-gradient-to-r from-primary to-accent transition-opacity',
            active === t ? 'opacity-100' : 'opacity-0'
          )} />
        </button>
      ))}
    </div>
  );
}

export function AssetPickerModal({ kind, selectedId, onClose, onSelect, onCreate }: Props) {
  const meta = titlesFor(kind);

  // Escape-to-close. Re-binds if onClose's identity changes (cheap, harmless).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Lock body scroll + stop any preview — ONLY on real mount/unmount, NEVER on a parent
  // re-render. The plan card passes a fresh onClose every render (4s poll); if stopPreview
  // lived in the [onClose] effect above, each poll's cleanup would kill an in-progress
  // voice preview the instant it started — which is exactly why the picker's play button
  // "did nothing" inside a plan card but worked on the (static) home page. []-deps = the
  // cleanup runs once, when the modal actually closes.
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      stopPreview();
    };
  }, []);

  // Record every pick into "Recently Used" before handing it to the parent.
  const select = (item: PickedAsset) => {
    pushRecent({ kind: item.kind, id: item.id, label: item.label, imageUrl: item.imageUrl, previewAudioUrl: item.previewAudioUrl });
    onSelect(item);
  };

  return createPortal(
    <div className="fixed inset-0 z-[130] flex items-center justify-center p-3 sm:p-6 bg-black/80 backdrop-blur-md animate-fade-in" onClick={onClose}>
      <div
        className="w-full max-w-6xl h-[90vh] flex flex-col rounded-3xl bg-[#15151d] border border-white/10 shadow-2xl animate-slide-up overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="shrink-0 px-6 sm:px-8 pt-6 pb-4 flex items-start gap-4">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold text-text-primary tracking-tight">{meta.title}</h2>
            <p className="text-sm text-text-tertiary mt-0.5">{meta.sub}</p>
          </div>
          <button onClick={onClose} className="shrink-0 w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-text-secondary hover:text-text-primary transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body — each body owns its toolbar (tabs + search + filters) */}
        {kind === 'avatar' && <AvatarBody selectedId={selectedId} onSelect={select} onCreate={onCreate} />}
        {kind === 'voice' && <VoiceBody selectedId={selectedId} onSelect={select} onCreate={onCreate} />}
        {kind === 'brand' && <BrandBody selectedId={selectedId} onSelect={select} onCreate={onCreate} />}
      </div>
    </div>,
    document.body
  );
}

// ---------------------------------------------------------------------------
// Shared toolbar pieces
// ---------------------------------------------------------------------------

function SearchInput({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <div className="relative w-full sm:w-64">
      <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-white/5 border border-white/10 rounded-full pl-10 pr-4 py-2.5 text-sm text-text-primary placeholder:text-text-tertiary outline-none focus:border-primary/50 focus:bg-white/[0.07] transition-colors"
      />
    </div>
  );
}

function GenderChips({ value, onChange }: { value: '' | 'female' | 'male'; onChange: (g: '' | 'female' | 'male') => void }) {
  const { t } = useTranslation('v2');
  const genderLabel = (g: '' | 'female' | 'male') =>
    g === 'female' ? t('assetPicker.genderFemale') : g === 'male' ? t('assetPicker.genderMale') : t('assetPicker.genderAll');
  return (
    <div className="flex gap-1.5 shrink-0">
      {(['', 'female', 'male'] as const).map((g) => (
        <button
          key={g || 'any'}
          onClick={() => onChange(g)}
          className={cn(
            'px-3.5 py-2 rounded-full text-xs font-semibold capitalize transition-colors',
            value === g ? 'bg-primary/15 border border-primary/40 text-primary' : 'bg-white/5 border border-transparent text-text-secondary hover:text-text-primary hover:bg-white/10'
          )}
        >
          {genderLabel(g)}
        </button>
      ))}
    </div>
  );
}

function Toolbar({ left, right }: { left: React.ReactNode; right: React.ReactNode }) {
  return (
    <div className="shrink-0 px-6 sm:px-8 border-b border-white/10 flex items-end justify-between gap-4 flex-wrap">
      {left}
      <div className="flex items-center gap-2 pb-2.5 flex-wrap">{right}</div>
    </div>
  );
}

function EmptyNote({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-text-tertiary py-12 text-center">{children}</p>;
}

// ---------------------------------------------------------------------------
// Shared selectable row — the ONE list-row used by all three pickers (avatar,
// voice, brand) so the menus look and feel identical: a 44px lead element
// (audio play-button for voices, image thumbnail for avatars/brands), a
// title + subtitle, an optional badge and a check when selected.
// ---------------------------------------------------------------------------
// Voice sample button — a real TOGGLE that reflects live state: Play → (buffering)
// spinner → Stop while playing → back to Play on end/stop. Clicking again STOPS it
// (the old button only ever restarted, so a preview was impossible to pause).
function VoicePreviewButton({ url }: { url?: string }) {
  const { t } = useTranslation('v2');
  const [, force] = useState(0);
  useEffect(() => onPreviewChange(() => force((n) => n + 1)), []);
  const playing = isPreviewPlaying(url);
  const loading = isPreviewLoading(url);
  return (
    <button
      onClick={(e) => { e.stopPropagation(); togglePreview(url); }}
      title={playing || loading ? t('assetPicker.stop') : t('assetPicker.playSample')}
      className="w-11 h-11 rounded-full bg-gradient-to-br from-primary/50 to-accent/50 flex items-center justify-center shrink-0 hover:scale-105 hover:shadow-glow transition-all"
    >
      {loading
        ? <Loader2 className="w-5 h-5 text-white animate-spin" />
        : playing
          ? <Square className="w-4 h-4 text-white fill-white" />
          : <Play className="w-5 h-5 text-white ml-0.5" />}
    </button>
  );
}

// Avatar/brand thumbnail that degrades gracefully: if the image url is missing OR fails to
// load (e.g. a recently-used avatar whose stored HeyGen url has expired), it falls back to an
// icon/initials instead of a broken-image box. Resets when the src changes.
function Thumb({ src, fallback }: { src?: string; fallback: React.ReactNode }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [src]);
  return (
    <span className="w-11 h-11 rounded-xl overflow-hidden bg-white/5 flex items-center justify-center shrink-0">
      {src && !failed
        ? <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} className="w-full h-full object-cover" />
        : fallback}
    </span>
  );
}

export function PickRow({
  img, icon: Icon, name, sub, badge, badgeTone = 'public', selected, onClick, onPreview, previewUrl, swatches, trailing, disabled,
}: {
  img?: string;
  icon?: typeof Play;
  name: string;
  sub?: string;
  badge?: string;
  badgeTone?: 'own' | 'public';
  selected?: boolean;
  onClick?: () => void;
  onPreview?: () => void;     // legacy voice preview (always-play) — kept for callers
  previewUrl?: string;        // voices: a sample url → render the toggle button
  swatches?: string[];        // brand kits: palette dots
  trailing?: React.ReactNode; // optional extra controls on the right
  disabled?: boolean;         // not-yet-ready (still generating / failed) → not selectable
}) {
  return (
    <div
      role="button"
      aria-disabled={disabled || undefined}
      onClick={disabled ? undefined : onClick}
      className={cn(
        'flex items-center gap-4 p-4 rounded-2xl border transition-all',
        disabled
          ? 'opacity-60 cursor-not-allowed border-white/10 bg-white/[0.03]'
          : cn(onClick && 'cursor-pointer',
              selected ? 'border-primary bg-primary/10' : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-primary/30')
      )}
    >
      {previewUrl ? (
        <VoicePreviewButton url={previewUrl} />
      ) : onPreview ? (
        <button onClick={(e) => { e.stopPropagation(); onPreview(); }}
          className="w-11 h-11 rounded-full bg-gradient-to-br from-primary/50 to-accent/50 flex items-center justify-center shrink-0 hover:scale-105 hover:shadow-glow transition-all">
          <Play className="w-5 h-5 text-white ml-0.5" />
        </button>
      ) : (
        <Thumb
          src={img}
          fallback={Icon
            ? <Icon className="w-5 h-5 text-text-tertiary" />
            : <span className="text-xs font-semibold text-text-tertiary uppercase">{name.slice(0, 2)}</span>}
        />
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-text-primary truncate">{name}</p>
        {sub && <p className="text-xs text-text-tertiary truncate mt-0.5">{sub}</p>}
        {!!swatches?.length && (
          <span className="flex items-center gap-1.5 mt-1.5">
            {swatches.map((c) => <span key={c} className="w-3.5 h-3.5 rounded-full border border-white/20" style={{ backgroundColor: c }} />)}
          </span>
        )}
      </div>
      {trailing}
      {badge && (
        <span className={cn('px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wide shrink-0',
          badgeTone === 'own' ? 'bg-primary/20 text-primary' : 'bg-white/10 text-text-secondary')}>
          {badge}
        </span>
      )}
      {selected && <Check className="w-4 h-4 text-primary shrink-0" />}
    </div>
  );
}

// One column on phones, two on wider screens — identical for all three menus.
export const pickList = 'grid grid-cols-1 lg:grid-cols-2 gap-3';

// ---------------------------------------------------------------------------
// Avatars — tabs: Recently Used | My Avatars | Public Avatars
// ---------------------------------------------------------------------------

const AVATAR_TABS = ['Recently Used', 'My Avatars', 'Public Avatars'] as const;
type AvatarTab = typeof AVATAR_TABS[number];

// Unified drill: a character's looks (local data) or a legacy group's looks (fetched).
interface DrillLook { avatar_id: string; label: string; preview_image_url?: string; gender?: string }
interface DrillState { title: string; img?: string; count?: number; looks: DrillLook[] | null; failed: boolean }

function mergeCharacters(prev: V2AvatarCharacter[], incoming: V2AvatarCharacter[]): V2AvatarCharacter[] {
  const map = new Map(prev.map((c) => [c.name, { ...c, looks: [...c.looks] }]));
  for (const c of incoming) {
    const hit = map.get(c.name);
    if (!hit) { map.set(c.name, c); continue; }
    const seen = new Set(hit.looks.map((l) => l.avatar_id));
    for (const lk of c.looks) if (!seen.has(lk.avatar_id)) hit.looks.push(lk);
    hit.looks_count = hit.looks.length;
  }
  return [...map.values()];
}

function AvatarBody({ selectedId, onSelect, onCreate }: {
  selectedId?: string; onSelect: (i: PickedAsset) => void; onCreate?: Props['onCreate'];
}) {
  const { t } = useTranslation('v2');
  const genderLabel = useGenderLabel();
  const userId = useUserStore((s) => s.userId);
  const recents = getRecents('avatar');
  const [tab, setTab] = useState<AvatarTab>(recents.length ? 'Recently Used' : 'Public Avatars');
  const [search, setSearch] = useState('');
  const [gender, setGender] = useState<'' | 'female' | 'male'>('');
  const [owned, setOwned] = useState<V2OwnedAvatar[]>([]);
  const [chars, setChars] = useState<V2AvatarCharacter[]>([]);
  const [legacyGroups, setLegacyGroups] = useState<V2AvatarGroup[] | null>(null);
  const [legacyToken, setLegacyToken] = useState<string | null>(null);
  const [drill, setDrill] = useState<DrillState | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const sentinel = useRef<HTMLDivElement>(null);

  const load = useCallback(async (off: number, token?: string | null) => {
    setLoading(true); setErr(null);
    try {
      const res = await v2Service.listAvatars({
        userId: userId ?? undefined, offset: off, token,
        gender: gender || undefined, search: search || undefined, limit: 120,
      });
      if (res.legacy_groups) {
        // Oldest deployed backend: groups + token paging, drill fetches looks.
        setLegacyGroups((prev) => (token && prev ? [...prev, ...res.legacy_groups!] : res.legacy_groups!));
        setLegacyToken(res.has_more ? res.legacy_next_token ?? null : null);
        setChars([]); setHasMore(false);
      } else {
        const incoming = res.characters || [];
        setChars((prev) => (off ? mergeCharacters(prev, incoming) : incoming));
        setHasMore(!!res.has_more);
        setOffset(res.next_offset ?? off);
        setLegacyGroups(null);
      }
      if (!off && !token) setOwned(res.owned || []);
    } catch {
      setErr(NOT_DEPLOYED());
    } finally { setLoading(false); }
  }, [userId, gender, search]);

  useEffect(() => {
    const t = setTimeout(() => void load(0), 300);
    return () => clearTimeout(t);
  }, [load]);

  // infinite scroll (characters mode)
  useEffect(() => {
    if (tab !== 'Public Avatars' || legacyGroups || !hasMore || loading) return;
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) void load(offset);
    }, { rootMargin: '300px' });
    io.observe(el);
    return () => io.disconnect();
  }, [tab, legacyGroups, hasMore, loading, offset, load]);

  // Character click: single look selects directly, otherwise open the local drill.
  const openCharacter = (c: V2AvatarCharacter) => {
    if (c.looks.length === 1) {
      const lk = c.looks[0];
      onSelect({ kind: 'avatar', id: lk.avatar_id, label: c.name, imageUrl: lk.preview_image_url, gender: lk.gender });
      return;
    }
    setDrill({
      title: c.name, img: c.preview_image_url, count: c.looks_count, failed: false,
      looks: c.looks.map((lk) => ({ avatar_id: lk.avatar_id, label: lk.label, preview_image_url: lk.preview_image_url, gender: lk.gender })),
    });
  };

  const openLegacyGroup = async (group: V2AvatarGroup) => {
    setDrill({ title: group.name, img: group.preview_image_url, count: group.looks_count, looks: null, failed: false });
    try {
      const res = await v2Service.listAvatarLooks(group.group_id);
      const looks = (res.looks || []).map((lk) => ({ avatar_id: lk.avatar_id, label: lk.name, preview_image_url: lk.preview_image_url, gender: lk.gender }));
      setDrill((d) => (d && d.title === group.name ? { ...d, looks, failed: looks.length === 0 } : d));
    } catch {
      setDrill((d) => (d && d.title === group.name ? { ...d, looks: [], failed: true } : d));
    }
  };

  const term = search.trim().toLowerCase();
  const shownRecents = recents.filter((r) => !term || r.label.toLowerCase().includes(term));
  // Live preview lookup for OWNED avatars (permanent S3 url) — a recent may hold a stale/expired
  // HeyGen url, so prefer the current one from the loaded owned list when the ids match.
  const ownedById = new Map(owned.filter((o) => o.avatar_id).map((o) => [o.avatar_id as string, o]));
  const readyOwned = owned.filter((a) => (a.status === 'completed' || a.status === 'ready') && (!term || a.name.toLowerCase().includes(term)));
  const pendingOwned = owned.filter((a) => a.status !== 'completed' && a.status !== 'ready');
  const shownGroups = (legacyGroups || []).filter((g) =>
    (!gender || g.gender === gender) && (!term || g.name.toLowerCase().includes(term)));
  const charSelected = (c: V2AvatarCharacter) => !!selectedId && c.looks.some((lk) => lk.avatar_id === selectedId);

  // ---- Looks drill-in (instant for characters; fetched for legacy groups) ----
  if (drill) {
    return (
      <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-5">
        <button onClick={() => setDrill(null)} className="flex items-center gap-1.5 text-sm font-semibold text-text-secondary hover:text-text-primary mb-5">
          <ChevronLeft className="w-4 h-4" /> {t('assetPicker.allAvatars')}
        </button>
        <div className="flex items-center gap-4 mb-6">
          {drill.img && <img src={drill.img} alt="" className="w-14 h-14 rounded-2xl object-cover" />}
          <div>
            <h3 className="text-lg font-bold text-text-primary">{drill.title}</h3>
            <p className="text-xs text-text-tertiary">{t('assetPicker.chooseLookCount', { num: drill.count ?? drill.looks?.length ?? '—' })}</p>
          </div>
        </div>
        {drill.looks === null && <div className="flex justify-center py-12 text-text-tertiary"><Loader2 className="w-6 h-6 animate-spin" /></div>}
        {drill.failed && (
          <EmptyNote>
            {t('assetPicker.packUnavailable')}<br />
            {t('assetPicker.pickAnotherAvatar')}
          </EmptyNote>
        )}
        {!!drill.looks?.length && (
          <div className={pickList}>
            {drill.looks.map((lk) => (
              <PickRow key={lk.avatar_id} name={lk.label} img={lk.preview_image_url} sub={genderLabel(lk.gender)} selected={lk.avatar_id === selectedId}
                onClick={() => onSelect({ kind: 'avatar', id: lk.avatar_id, label: `${drill.title} — ${lk.label}`, imageUrl: lk.preview_image_url, gender: lk.gender })} />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      {/* Avatar creation methods laid out at the top, exactly like the voice menu */}
      {onCreate && (
        <div className="shrink-0 px-6 sm:px-8 pb-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <button onClick={() => onCreate('avatar-ai')} className="flex items-center justify-center gap-2 py-3 rounded-2xl bg-white/5 border border-white/10 text-sm font-semibold text-text-primary hover:bg-white/10 hover:border-primary/30 transition-colors">
            <Wand2 className="w-4 h-4 text-primary" /> {t('assetPicker.createWithAi')}
          </button>
          <button onClick={() => onCreate('avatar-photo')} className="flex items-center justify-center gap-2 py-3 rounded-2xl bg-white/5 border border-white/10 text-sm font-semibold text-text-primary hover:bg-white/10 hover:border-primary/30 transition-colors">
            <Camera className="w-4 h-4 text-primary" /> {t('assetPicker.photoToAvatar')}
          </button>
          {/* Digital twin temporarily disabled (training/consent flow not reliable yet). */}
          <button onClick={() => toast.info(t('assetPicker.digitalTwinComingSoon'))} className="relative flex items-center justify-center gap-2 py-3 rounded-2xl bg-white/5 border border-white/10 text-sm font-semibold text-text-tertiary opacity-70 cursor-default">
            <Video className="w-4 h-4 text-text-tertiary" /> {t('assetPicker.digitalTwin')}
            <span className="absolute top-1 right-2 px-1.5 py-0.5 rounded-full bg-primary/15 text-primary text-[9px] font-bold uppercase">{t('assetPicker.soon')}</span>
          </button>
        </div>
      )}

      <Toolbar
        left={<Tabs tabs={AVATAR_TABS} active={tab} onChange={setTab} label={(t2) =>
          t2 === 'Recently Used' ? t('assetPicker.tabRecentlyUsed')
            : t2 === 'My Avatars' ? t('assetPicker.tabMyAvatars')
            : t('assetPicker.tabPublicAvatars')} />}
        right={<>
          <SearchInput value={search} onChange={setSearch} placeholder={t('assetPicker.searchAvatars')} />
          {tab === 'Public Avatars' && <GenderChips value={gender} onChange={setGender} />}
        </>}
      />
      <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-5">
        {err && <p className="text-xs text-amber-300/80 mb-4">{err}</p>}

        {tab === 'Recently Used' && (
          shownRecents.length ? (
            <div className={pickList}>
              {shownRecents.map((r) => {
                const freshImg = ownedById.get(r.id)?.preview_image_url || r.imageUrl;
                return (
                  <PickRow key={r.id} name={r.label} img={freshImg} selected={r.id === selectedId}
                    onClick={() => onSelect({ kind: 'avatar', id: r.id, label: r.label, imageUrl: freshImg })} />
                );
              })}
            </div>
          ) : <EmptyNote>{t('assetPicker.recentAvatarsEmpty')}</EmptyNote>
        )}

        {tab === 'My Avatars' && (
          <div className={pickList}>
            {readyOwned.map((a) => <OwnedRow key={a.avatar_ref} a={a} selected={!!a.avatar_id && a.avatar_id === selectedId} onSelect={onSelect} />)}
            {pendingOwned.map((a) => <PendingOwnedRow key={a.avatar_ref} a={a} onChanged={() => void load(0)} />)}
            {!readyOwned.length && !pendingOwned.length && <EmptyNote>{t('assetPicker.myAvatarsEmpty')}</EmptyNote>}
          </div>
        )}

        {tab === 'Public Avatars' && (
          <>
            {legacyGroups ? (
              <>
                <div className={pickList}>
                  {shownGroups.map((g) => (
                    <PickRow key={g.group_id} name={g.name} img={g.preview_image_url} sub={t('assetPicker.looksCount', { num: g.looks_count ?? '—' })} badge={t('assetPicker.badgePack')}
                      onClick={() => void openLegacyGroup(g)} />
                  ))}
                </div>
                {legacyToken && (
                  <div className="flex justify-center mt-6">
                    <button onClick={() => void load(0, legacyToken)} disabled={loading}
                      className="px-6 py-2.5 rounded-full bg-white/5 border border-white/10 text-sm font-semibold text-text-secondary hover:text-text-primary hover:bg-white/10 transition-colors disabled:opacity-50">
                      {loading ? t('assetPicker.loading') : t('assetPicker.loadMore')}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* One row per CHARACTER — click to choose among its looks */}
                <div className={pickList}>
                  {chars.map((c) => (
                    <PickRow key={c.name} name={c.name}
                      img={c.preview_image_url}
                      sub={c.looks_count > 1 ? t('assetPicker.looksCount', { num: c.looks_count }) : genderLabel(c.gender)}
                      badge={t('assetPicker.badgePublic')}
                      selected={charSelected(c)}
                      onClick={() => openCharacter(c)} />
                  ))}
                </div>
                <div ref={sentinel} className="h-1" />
              </>
            )}
            {loading && <div className="flex justify-center py-8 text-text-tertiary"><Loader2 className="w-6 h-6 animate-spin" /></div>}
          </>
        )}
      </div>
    </>
  );
}

/** Ready owned avatar row — self-heals its preview image + look id if missing. */
function OwnedRow({ a, selected, onSelect }: { a: V2OwnedAvatar; selected: boolean; onSelect: (i: PickedAsset) => void }) {
  const { t } = useTranslation('v2');
  const userId = useUserStore((s) => s.userId);
  const [img, setImg] = useState<string | undefined>(a.preview_image_url);
  const [lookId, setLookId] = useState<string | undefined>(a.avatar_id);

  useEffect(() => {
    if ((img && lookId) || !userId) return;
    let on = true;
    void resolveOwnedAvatarMedia(userId, a.avatar_ref).then((m) => {
      if (!on) return;
      if (m.img) setImg((v) => v || m.img);
      if (m.lookId) setLookId((v) => v || m.lookId);
    });
    return () => { on = false; };
  }, [userId, a.avatar_ref, img, lookId]);

  return (
    <PickRow
      name={a.name}
      img={img}
      sub={a.kind}
      badge={t('assetPicker.badgeYours')}
      badgeTone="own"
      selected={selected}
      onClick={() => lookId && onSelect({ kind: 'avatar', id: lookId, label: a.name, imageUrl: img })}
    />
  );
}

/** Training / consent-pending owned avatar row (not yet pickable). */
function PendingOwnedRow({ a, onChanged }: { a: V2OwnedAvatar; onChanged: () => void }) {
  const { t } = useTranslation('v2');
  const userId = useUserStore((s) => s.userId);
  const [busy, setBusy] = useState(false);
  const needsConsent = a.status === 'pending_consent';
  const failed = a.status === 'failed' || a.status === 'error';

  // Auto-poll while it's still generating so the avatar becomes selectable LIVE in the picker
  // (it moves to the ready list the moment it's done) — no manual refresh, no picking it early.
  useEffect(() => {
    if (!userId || needsConsent || failed) return;
    let on = true;
    let timer: ReturnType<typeof setTimeout>;
    const tick = async () => {
      try {
        const st = await v2Service.avatarStatus(userId, a.avatar_ref);
        if (!on) return;
        if (['completed', 'ready', 'failed', 'error'].includes(String(st.status))) { onChanged(); return; }
      } catch { /* keep trying */ }
      if (!on) return;
      timer = setTimeout(() => void tick(), 4000);
    };
    timer = setTimeout(() => void tick(), 3000);
    return () => { on = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, a.avatar_ref, needsConsent, failed]);

  const refresh = async () => {
    if (!userId || busy) return;
    setBusy(true);
    try { await v2Service.avatarStatus(userId, a.avatar_ref); onChanged(); }
    catch { /* pre-deploy */ } finally { setBusy(false); }
  };
  const consent = async () => {
    if (!userId) return;
    try {
      const r = await v2Service.createAvatarConsent(userId, a.avatar_ref, window.location.href);
      if (r.consent_url) {
        window.open(r.consent_url, '_blank', 'noopener');
        toast.info(t('assetPicker.consentOpened'));
      } else {
        toast.error(t('assetPicker.consentUnavailable'));
      }
    } catch { toast.error(t('assetPicker.consentUnavailable')); }
  };

  return (
    <PickRow
      name={a.name}
      img={a.preview_image_url}
      sub={needsConsent ? t('assetPicker.statusNeedsConsent') : failed ? t('assetPicker.statusFailed') : t('assetPicker.statusGenerating')}
      disabled
      trailing={
        <span className="flex items-center gap-1.5 shrink-0">
          {failed ? (
            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-red-500/85 text-white">{t('assetPicker.failedBadge')}</span>
          ) : needsConsent ? (
            <button onClick={(e) => { e.stopPropagation(); void consent(); }} className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-amber-400 text-black text-[10px] font-bold">
              <ShieldCheck className="w-3 h-3" /> {t('assetPicker.consentButton')}
            </button>
          ) : (
            <button onClick={(e) => { e.stopPropagation(); void refresh(); }} className="w-7 h-7 rounded-full bg-white/5 flex items-center justify-center text-text-tertiary hover:text-text-primary">
              <RefreshCw className={cn('w-3.5 h-3.5', busy && 'animate-spin')} />
            </button>
          )}
        </span>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Voices — create actions + tabs: My voices | HeyGen library
// ---------------------------------------------------------------------------

const VOICE_TABS = ['My voices', 'Public library'] as const;
type VoiceTab = typeof VOICE_TABS[number];

function VoiceBody({ selectedId, onSelect, onCreate }: {
  selectedId?: string; onSelect: (i: PickedAsset) => void; onCreate?: Props['onCreate'];
}) {
  const { t } = useTranslation('v2');
  const genderLabel = useGenderLabel();
  const userId = useUserStore((s) => s.userId);
  const [tab, setTab] = useState<VoiceTab>('Public library');
  const [search, setSearch] = useState('');
  const [gender, setGender] = useState<'' | 'female' | 'male'>('');
  const [owned, setOwned] = useState<V2OwnedVoice[]>([]);
  const [stock, setStock] = useState<V2Voice[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const primeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const res = await v2Service.listVoices({ userId: userId ?? undefined, gender: gender || undefined, search: search || undefined, limit: 60 });
      setOwned(res.owned || []);
      setStock(res.voices || []);
      // Cold cache → backend serves a partial first page and warms the rest;
      // re-fetch shortly so the full catalogue fills in automatically.
      if (res.cache_priming) {
        if (primeTimer.current) clearTimeout(primeTimer.current);
        primeTimer.current = setTimeout(() => void load(), 4000);
      }
    } catch { setErr(NOT_DEPLOYED()); } finally { setLoading(false); }
  }, [userId, gender, search]);

  useEffect(() => { const t = setTimeout(() => void load(), 300); return () => clearTimeout(t); }, [load]);
  useEffect(() => () => { if (primeTimer.current) clearTimeout(primeTimer.current); }, []);

  // Render <PickRow> DIRECTLY below (like AvatarBody/BrandBody) — do NOT wrap it in a
  // Row component defined here. An inline component is a brand-new type on every render,
  // so React REMOUNTS every voice row whenever this body re-renders. Inside the agentic
  // page (which re-renders on each 4s poll) that tore the rows down continuously — clicks
  // never landed and the play/preview buttons never stabilised, while home (static) looked
  // fine. PickRow is a stable top-level component, so direct use just updates in place.
  return (
    <>
      {/* Create actions — laid out at the top (same as the avatar menu) */}
      <div className="shrink-0 px-6 sm:px-8 pb-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button onClick={() => onCreate?.('voice-clone')} className="flex items-center justify-center gap-2 py-3 rounded-2xl bg-white/5 border border-white/10 text-sm font-semibold text-text-primary hover:bg-white/10 hover:border-primary/30 transition-colors">
          <Mic className="w-4 h-4 text-primary" /> {t('assetPicker.cloneVoice')}
        </button>
        <button onClick={() => onCreate?.('voice-design')} className="flex items-center justify-center gap-2 py-3 rounded-2xl bg-white/5 border border-white/10 text-sm font-semibold text-text-primary hover:bg-white/10 hover:border-primary/30 transition-colors">
          <Sparkles className="w-4 h-4 text-primary" /> {t('assetPicker.designVoice')}
        </button>
      </div>

      <Toolbar
        left={<Tabs tabs={VOICE_TABS} active={tab} onChange={setTab} label={(t2) =>
          t2 === 'My voices' ? t('assetPicker.tabMyVoices') : t('assetPicker.tabPublicLibrary')} />}
        right={<>
          <SearchInput value={search} onChange={setSearch} placeholder={t('assetPicker.searchVoices')} />
          {tab === 'Public library' && <GenderChips value={gender} onChange={setGender} />}
        </>}
      />

      <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-5">
        {err && <p className="text-xs text-amber-300/80 mb-4">{err}</p>}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {tab === 'My voices' && owned.filter((v) => v.status === 'ready').map((v) => (
            <PickRow key={v.voice_ref} name={v.name}
              sub={[v.language, genderLabel(v.gender)].filter(Boolean).join(' · ') || v.kind}
              icon={Mic} previewUrl={v.preview_audio_url}
              badge={v.kind === 'clone' ? t('assetPicker.badgeVoiceClone') : t('assetPicker.badgeVoiceDesign')} badgeTone="own"
              selected={v.voice_id === selectedId}
              onClick={() => onSelect({ kind: 'voice', id: v.voice_id, label: v.name, previewAudioUrl: v.preview_audio_url })} />
          ))}
          {tab === 'Public library' && stock.map((v) => (
            <PickRow key={v.voice_id} name={v.name}
              sub={[genderLabel(v.gender), v.language].filter(Boolean).join(' · ')}
              icon={Mic} previewUrl={v.preview_audio_url}
              badge={t('assetPicker.badgePublic')} badgeTone="public"
              selected={v.voice_id === selectedId}
              onClick={() => onSelect({ kind: 'voice', id: v.voice_id, label: v.name, previewAudioUrl: v.preview_audio_url })} />
          ))}
        </div>
        {tab === 'My voices' && !loading && !owned.some((v) => v.status === 'ready') && (
          <EmptyNote>{t('assetPicker.myVoicesEmpty')}</EmptyNote>
        )}
        {tab === 'My voices' && owned.some((v) => v.status === 'processing') && (
          <p className="text-xs text-text-tertiary mt-4 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {t('assetPicker.voicesProcessing')}</p>
        )}
        {loading && <div className="flex justify-center py-8 text-text-tertiary"><Loader2 className="w-6 h-6 animate-spin" /></div>}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Brand kits — the user's own kits (logo + palette + fonts + identity).
// Applied invisibly at generation time (logo attach + hidden injection).
// ---------------------------------------------------------------------------

function BrandBody({ selectedId, onSelect, onCreate }: {
  selectedId?: string; onSelect: (i: PickedAsset) => void; onCreate?: Props['onCreate'];
}) {
  const { t } = useTranslation('v2');
  const userId = useUserStore((s) => s.userId);
  const [brands, setBrands] = useState<V2Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return;
    let on = true;
    (async () => {
      setLoading(true);
      try {
        const res = await v2Service.listBrands(userId);
        if (on) setBrands(res.brands || []);
      } catch {
        if (on) setErr(NOT_DEPLOYED());
      } finally {
        if (on) setLoading(false);
      }
    })();
    return () => { on = false; };
  }, [userId]);

  return (
    <>
      {/* Create action at the top, mirroring the voice menu */}
      <div className="shrink-0 px-6 sm:px-8 pb-4">
        <button onClick={() => onCreate?.('brand-new')} className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl bg-white/5 border border-white/10 text-sm font-semibold text-text-primary hover:bg-white/10 hover:border-primary/30 transition-colors">
          <Plus className="w-4 h-4 text-primary" /> {t('assetPicker.newBrandKit')}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-5">
        {err && <p className="text-xs text-amber-300/80 mb-4">{err}</p>}
        <div className={pickList}>
          {brands.map((b) => {
            const palette = [b.primary_color, b.secondary_color, b.tertiary_color, b.accent_color].filter(Boolean) as string[];
            const sub = [t('assetPicker.motionStyle', { style: b.motion_style || t('assetPicker.motionDefault') }), [b.title_font, b.body_font].filter(Boolean).join(' · ')].filter(Boolean).join(' · ');
            return (
              <PickRow
                key={b.brand_ref}
                name={b.name}
                img={b.logo_url}
                icon={Sparkles}
                sub={sub}
                swatches={palette}
                badge={t('assetPicker.badgeBrand')}
                badgeTone="own"
                selected={b.brand_ref === selectedId}
                onClick={() => onSelect({ kind: 'brand', id: b.brand_ref, label: b.name, imageUrl: b.logo_url })}
              />
            );
          })}
        </div>
        {loading && <div className="flex justify-center py-8 text-text-tertiary"><Loader2 className="w-6 h-6 animate-spin" /></div>}
        {!loading && !brands.length && !err && (
          <EmptyNote>{t('assetPicker.brandsEmpty')}</EmptyNote>
        )}
      </div>
    </>
  );
}
